/**
 * 주요항목 가격표 (Phase 9) — 큰 탭 2개로 구성:
 *
 *   1) 주요제품 판매가 — DB 운영 CRUD (15건 시드).
 *   2) 인포트렌드 원가표 — DB 운영 (분기별 SMSYSTEMS 가격표 .xlsx 일괄 업로드, ~9,500 부품).
 *
 * 권한:
 *   - viewer: 공급단가 컬럼 hidden (소비자단가만)
 *   - sales+: 모든 컬럼
 *   - sales+: 인포트렌드 탭에 [📤 신규 분기 업로드] / [✕ 이번 분기 삭제(직전 분기 자동 승격)] 활성화
 */

import { lazy, Suspense, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import { LastUpdateBadge } from '../components/LastUpdateBadge';
import { VatNote } from '../components/VatNote';
import { SearchBar } from '../components/SearchBar';
import * as priceApi from '../lib/price-list-api';
import { PageTabs } from '../components/ui/PageTabs';
import type { MainProductRow as ApiMainProductRow } from '../lib/price-list-api';
import {
  listInfortrendPrice,
  listInfortrendMetas,
  deleteInfortrendMeta,
  type InfortrendRow,
  type InfortrendMeta,
} from '../lib/infortrend-api';
import { LoadingLines } from '../components/ui/Skeleton';

const MainProductFormModal = lazy(() =>
  import('../components/MainProductFormModal').then((m) => ({
    default: m.MainProductFormModal,
  })),
);

const InfortrendUploadModal = lazy(() =>
  import('../components/InfortrendUploadModal').then((m) => ({
    default: m.InfortrendUploadModal,
  })),
);

// 🧮 원가분석 (v2.234.0) — 매입 목록·인포트렌드 가격표·직접 입력으로 원가를 모아 마진을 보고 견적서로 넘긴다
const CostAnalysisTab = lazy(() =>
  import('../components/cost-sheet/CostAnalysisTab').then((m) => ({
    default: m.CostAnalysisTab,
  })),
);

type MainTab = 'main' | 'infortrend' | 'cost';
const isMainTab = (v: string | null): v is MainTab => v === 'main' || v === 'infortrend' || v === 'cost';

export function PriceListPage() {
  // ?tab=cost 로 원가분석을 바로 열 수 있다 (v2.234.0 · 다른 화면에서 링크로 보낼 때) — 바꿀 때 주소도 같이
  const [sp, setSp] = useSearchParams();
  const rawTab = sp.get('tab');
  const mainTab: MainTab = isMainTab(rawTab) ? rawTab : 'main';
  const setMainTab = (t: MainTab) => { const next = new URLSearchParams(sp); next.set('tab', t); setSp(next, { replace: true }); };

  return (
    <div className="p-8">
      {/* 페이지 헤더 */}
      <div className="mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="ui-page-title">주요항목 가격표</h1>
          <VatNote />
        </div>
        <LastUpdateBadge className="mt-1" />
      </div>

      {/* 큰 탭 3개 */}
      <PageTabs
        tabs={[
          { key: 'main', label: '💰 주요제품 판매가' },
          { key: 'infortrend', label: '📦 인포트렌드 원가표' },
          { key: 'cost', label: '🧮 원가분석' },
        ]}
        value={mainTab}
        onChange={setMainTab}
        ariaLabel="가격표 보기"
      />

      {mainTab === 'main' && <MainProductTab />}
      {mainTab === 'infortrend' && <InfortrendCostTab />}
      {mainTab === 'cost' && (
        <Suspense fallback={<div className="text-sm text-slate-400 py-10 text-center"><LoadingLines /></div>}>
          <CostAnalysisTab />
        </Suspense>
      )}
    </div>
  );
}


function fmtKrw(n: number | null | undefined): string {
  if (n === null || n === undefined) return '-';
  return n.toLocaleString('ko-KR');
}

// ============================================================
// 탭 1 — 주요제품 판매가 (DB CRUD, 2026-05-18 본 운영)
// ============================================================
//
// 운영 데이터: main_product_price 테이블. 초기 시드는 운영 견적서 시트의 15건.
// 권한:
//   - viewer: 공급단가 hidden (소비자단가만)
//   - sales+admin: 추가 / 수정 / 삭제 가능

// (참고용 mockup 15건은 DB 시드 `db/migration_2026_main_product_price.sql` 이관 후 2026-07-19 제거.)

function MainProductTab() {
  const { user } = useAuth();
  const showCost = user?.role === 'admin' || user?.role === 'mgmt' || user?.role === 'sales';
  const canEdit = user?.role === 'admin' || user?.role === 'mgmt' || user?.role === 'sales';
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ApiMainProductRow | null>(null);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['price-list', 'main'],
    queryFn: priceApi.listMainProducts,
    staleTime: 60_000,
  });
  const allRows = query.data ?? [];

  const deleteMut = useMutation({
    mutationFn: (id: number) => priceApi.deleteMainProduct(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['price-list', 'main'] });
    },
    onError: (e: Error) => window.alert(`삭제 실패: ${e.message}`),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((r) => {
      const blob = [r.name, r.description, r.note ?? '', r.category].join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [allRows, search]);

  // 검색바 자동완성 후보 — 품목명·카테고리·비고 첫 줄
  const searchSuggestions = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const r of allRows) {
      if (r.name) set.add(r.name);
      if (r.category) set.add(r.category);
      if (r.note) {
        const firstLine = r.note.split('\n')[0]?.trim();
        if (firstLine) set.add(firstLine);
      }
    }
    return [...set];
  }, [allRows]);

  // 카테고리 별 그룹
  const groups = useMemo(() => {
    const map = new Map<string, ApiMainProductRow[]>();
    for (const r of filtered) {
      if (!map.has(r.category)) map.set(r.category, []);
      map.get(r.category)!.push(r);
    }
    // 스토리지 → 서버 → SW → 기타 순 (v2.230.0) · 옛 구분은 뒤에 — 전에는 정렬순서상 먼저 나온 구분이 위였다
    return Array.from(map.entries()).sort(([a], [b]) =>
      priceApi.mainCategoryRank(a) - priceApi.mainCategoryRank(b) || a.localeCompare(b, 'ko'));
  }, [filtered]);

  return (
    <>
      {/* 안내 + 검색 + 추가 버튼 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-4 mb-4">
        <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
          <div className="text-sm text-slate-500">
            ※ VAT 별도 금액입니다. 행 클릭 시 수정 / 삭제 가능 (권한자만)
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600">
              {filtered.length} / {allRows.length}건
            </span>
            {canEdit && (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="btn btn-primary"
              >
                + 추가
              </button>
            )}
          </div>
        </div>
        <SearchBar
          value={search}
          onCommit={setSearch}
          placeholder="🔍 품목명 / 상세내용 / 비고 / 카테고리"
          live
          suggestions={searchSuggestions}
        />
      </div>

      {/* 카테고리 별 표 */}
      {query.isLoading && (
        <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400 text-sm"><LoadingLines /></div>
      )}
      {query.isError && (
        <div className="bg-white rounded border border-red-200 p-12 text-center text-red-600 text-sm">
          오류: {(query.error as Error).message}
        </div>
      )}
      {!query.isLoading && !query.isError && groups.length === 0 && (
        <div className="bg-white rounded border border-slate-200 p-12 text-center text-slate-400 text-sm">
          조건에 맞는 제품이 없습니다.
        </div>
      )}
      {groups.map(([cat, rows]) => (
        <section
          key={cat}
          className="bg-white rounded shadow-sm border border-slate-200 mb-4 overflow-hidden"
        >
          <header className="px-4 py-2.5 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
            <h2 className="ui-section-title text-blue-800">{cat}</h2>
            <span className="text-xs text-blue-700">{rows.length}건</span>
          </header>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                <tr className="text-slate-700 text-xs">
                  <th className="text-left px-3 py-2 font-semibold w-40">품목</th>
                  <th className="text-left px-3 py-2 font-semibold">상세내용</th>
                  <th className="text-right px-3 py-2 font-semibold w-32">소비자단가</th>
                  {showCost && (
                    <th className="text-right px-3 py-2 font-semibold w-32">공급단가</th>
                  )}
                  <th className="text-left px-3 py-2 font-semibold w-48">비고</th>
                  {canEdit && (
                    <th className="text-center px-3 py-2 font-semibold w-20">동작</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-b border-slate-100 align-top ${
                      canEdit ? 'cursor-pointer hover:bg-slate-50' : ''
                    }`}
                    onClick={() => {
                      if (canEdit) setEditing(r);
                    }}
                  >
                    <td className="px-3 py-2.5 font-semibold text-slate-800">{r.name}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-700 whitespace-pre-line leading-relaxed">
                      {r.description}
                    </td>
                    <td className="px-3 py-2.5 text-right num font-semibold text-blue-700">
                      {fmtKrw(r.retailPrice)}
                    </td>
                    {showCost && (
                      <td className="px-3 py-2.5 text-right num text-slate-700">
                        {fmtKrw(r.supplyPrice)}
                      </td>
                    )}
                    <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-pre-line">
                      {r.note ?? <span className="text-slate-300">-</span>}
                    </td>
                    {canEdit && (
                      <td className="px-3 py-2.5 text-center">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`"${r.name}" 항목을 삭제하시겠습니까?`)) {
                              deleteMut.mutate(r.id);
                            }
                          }}
                          disabled={deleteMut.isPending}
                          className="text-xs text-rose-500 hover:text-rose-700 disabled:opacity-30"
                          title="삭제"
                        >
                          🗑
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      <Suspense fallback={null}>
        {creating && (
          <MainProductFormModal
            mode="create"
            onClose={() => setCreating(false)}
            onSaved={() => setCreating(false)}
          />
        )}
        {editing && (
          <MainProductFormModal
            mode="edit"
            initial={editing}
            onClose={() => setEditing(null)}
            onSaved={() => setEditing(null)}
          />
        )}
      </Suspense>
    </>
  );
}

// ============================================================
// 탭 2 — 인포트렌드 원가표 (DB 운영, 분기별 엑셀 일괄 업로드)
// ============================================================

// (Phase 9-1 mockup PRICE_LIST 28건 → Phase 9-2 DB 운영 전환 — 2026-05-18 PR #26)
// 화면은 GET /price-list/infortrend/list 로 DB 에서 가져오고,
// 분기별 엑셀은 admin 이 [📤 신규 분기 업로드] 로 통째로 교체.

const PRICE_CATEGORIES: { key: string; label: string; cls: string }[] = [
  { key: 'all',        label: '전체',       cls: 'bg-slate-100 text-slate-700' },
  { key: 'CS',         label: 'CS',         cls: 'bg-violet-100 text-violet-700' },
  { key: 'GS',         label: 'GS',         cls: 'bg-blue-100 text-blue-700' },
  { key: 'GSe',        label: 'GSe',        cls: 'bg-cyan-100 text-cyan-700' },
  { key: 'GSx',        label: 'GSx',        cls: 'bg-teal-100 text-teal-700' },
  { key: 'KS',         label: 'KS',         cls: 'bg-emerald-100 text-emerald-700' },
  { key: 'ESDS',       label: 'ESDS',       cls: 'bg-lime-100 text-lime-700' },
  { key: 'EV',         label: 'EV',         cls: 'bg-yellow-100 text-yellow-700' },
  { key: 'JBOD_SUB',   label: 'Expansion',  cls: 'bg-amber-100 text-amber-700' },
  { key: 'JBOD_SRV',   label: 'JBOD',       cls: 'bg-orange-100 text-orange-700' },
  { key: 'FRU',        label: 'FRU Parts',  cls: 'bg-rose-100 text-rose-700' },
  { key: 'HOSTBOARD',  label: 'Host Board', cls: 'bg-pink-100 text-pink-700' },
  { key: 'SOFTWARE',   label: 'Software',   cls: 'bg-fuchsia-100 text-fuchsia-700' },
  { key: 'HDD',        label: 'HDD',        cls: 'bg-indigo-100 text-indigo-700' },
  { key: 'CONTROLLER', label: 'Controller', cls: 'bg-purple-100 text-purple-700' },
  { key: 'HBA',        label: 'HBA',        cls: 'bg-sky-100 text-sky-700' },
  { key: 'WARRANTY',   label: 'Warranty',   cls: 'bg-stone-100 text-stone-700' },
];

const CAT_LOOKUP: Record<string, { label: string; cls: string }> = Object.fromEntries(
  PRICE_CATEGORIES.map((c) => [c.key, { label: c.label, cls: c.cls }]),
);

function statusClass(s: string | null): string {
  if (s === 'Current') return 'text-emerald-600';
  if (s === 'EOL Plan') return 'text-amber-600';
  if (s === 'EOL') return 'text-red-600';
  return 'text-slate-400';
}

function changeClass(n: number): string {
  if (n > 0) return 'text-red-600';
  if (n < 0) return 'text-blue-600';
  return 'text-slate-400';
}

function InfortrendCostTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const showCost = user?.role === 'admin' || user?.role === 'mgmt' || user?.role === 'sales';
  // 분기 업로드 / 분기 삭제 — 다른 가격표 CRUD 와 동일하게 sales+admin.
  const canUpload = user?.role === 'admin' || user?.role === 'mgmt' || user?.role === 'sales';

  const [category, setCategory] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const [status, setStatus] = useState<string>('all');
  const [showUpload, setShowUpload] = useState<boolean>(false);
  // 분기 드롭다운에서 선택한 metaId. null = 현재 분기(is_current).
  const [selectedMetaId, setSelectedMetaId] = useState<number | null>(null);

  // 분기 메타 목록 (드롭다운 옵션)
  const metasQuery = useQuery({
    queryKey: ['price-list', 'infortrend', 'metas'],
    queryFn: listInfortrendMetas,
    staleTime: 60_000,
  });
  const metas: InfortrendMeta[] = metasQuery.data?.metas ?? [];

  // 부품 데이터 (선택 분기)
  const { data, isLoading, error } = useQuery({
    queryKey: ['price-list', 'infortrend', selectedMetaId ?? 'current'],
    queryFn: () => listInfortrendPrice(selectedMetaId),
    staleTime: 60_000,
  });

  const meta = data?.meta ?? null;
  const rows: InfortrendRow[] = data?.rows ?? [];
  const isViewingPast = meta !== null && meta.is_current === false;

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteInfortrendMeta(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['price-list', 'infortrend'] });
      setSelectedMetaId(null); // 삭제 후 자동으로 현재 분기로 돌아감
      window.alert(`'${r.quarterLabel}' 분기 삭제 완료`);
    },
    onError: (e) => {
      window.alert(`삭제 실패: ${(e as Error).message}`);
    },
  });

  const filtered = useMemo<InfortrendRow[]>(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((p) => {
      if (category !== 'all' && p.cat !== category) return false;
      if (status !== 'all' && p.status !== status) return false;
      if (q) {
        const blob = [p.pn, p.model, p.subsystem, p.description, p.series, p.family]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [rows, category, search, status]);

  // 검색바 자동완성 후보 (PN/Model/Subsystem/Series/Family — Description 은 너무 길어 제외)
  const searchSuggestions = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const p of rows) {
      if (p.pn) set.add(p.pn);
      if (p.model) set.add(p.model);
      if (p.subsystem) set.add(p.subsystem);
      if (p.series) set.add(p.series);
      if (p.family) set.add(p.family);
    }
    return [...set];
  }, [rows]);

  function onDeleteClick() {
    if (!meta) return;
    const msg = isViewingPast
      ? `'${meta.quarter_label}' 분기를 통째로 삭제합니다. (${meta.row_count.toLocaleString('ko-KR')}건)\n복구할 수 없습니다. 진행할까요?`
      : `현재 분기 '${meta.quarter_label}' 를 삭제합니다. 직전 분기가 자동으로 새 현재 분기가 됩니다.\n진행할까요?`;
    if (!window.confirm(msg)) return;
    deleteMut.mutate(meta.id);
  }

  return (
    <>
      {/* 분기 드롭다운 + 메타 + 액션 버튼 */}
      <div className="flex justify-between items-start mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {/* 분기 선택 드롭다운 */}
          {metas.length > 0 && (
            <select
              value={selectedMetaId ?? ''}
              onChange={(e) => setSelectedMetaId(e.target.value ? Number(e.target.value) : null)}
              className="ui-input"
              title="분기 선택"
            >
              <option value="">📌 현재 분기 (자동)</option>
              {metas.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.is_current ? '★ ' : ''}
                  {m.quarter_label}
                  {m.validity_date ? ` (적용 ${m.validity_date})` : ''}
                  {' · '}
                  {m.row_count.toLocaleString('ko-KR')}건
                </option>
              ))}
            </select>
          )}

          <div className="text-sm text-slate-600">
            {meta ? (
              <>
                <strong className="text-slate-900">{meta.quarter_label}</strong>
                {meta.validity_date && (
                  <span className="ml-1 text-slate-500">(적용일 {meta.validity_date})</span>
                )}
                {' · '}
                <span className="text-slate-500">
                  {meta.row_count.toLocaleString('ko-KR')}건 / {meta.sheet_count}개 시트
                </span>
                {meta.uploaded_by && (
                  <span className="ml-1 text-xs text-slate-500">— 업로드 {meta.uploaded_by}</span>
                )}
              </>
            ) : isLoading ? (
              <span className="text-slate-400"><LoadingLines /></span>
            ) : (
              <span className="text-amber-600">
                아직 가격표가 업로드되지 않았습니다. {canUpload && '우상단 [📤] 으로 업로드하세요.'}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {canUpload && meta && (
            <button
              type="button"
              onClick={onDeleteClick}
              disabled={deleteMut.isPending}
              className="btn btn-danger"
              title={isViewingPast ? '이 과거 분기 삭제' : '현재 분기 삭제 (직전 분기가 새 현재 분기가 됨)'}
            >
              ✕ {isViewingPast ? '이 분기 삭제' : '이번 분기 삭제'}
            </button>
          )}
          {canUpload && (
            <button
              type="button"
              onClick={() => setShowUpload(true)}
              className="btn btn-primary"
            >
              📤 신규 분기 업로드
            </button>
          )}
        </div>
      </div>

      {/* 과거 분기 안내 배너 */}
      {isViewingPast && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-amber-800 text-xs flex items-center justify-between">
          <span>
            🕒 <strong>과거 분기</strong>를 보고 있습니다 — 참고용 (현재 분기가 아닙니다)
          </span>
          <button
            type="button"
            onClick={() => setSelectedMetaId(null)}
            className="text-xs text-amber-700 underline hover:text-amber-900"
          >
            현재 분기로 돌아가기
          </button>
        </div>
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded p-3 text-rose-700 text-sm mb-3">
          데이터 로딩 실패: {(error as Error).message}
        </div>
      )}

      {/* 검색 바 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-4 mb-4 sticky top-0 z-10">
        <div className="grid grid-cols-12 gap-3 items-end">
          <div className="col-span-6">
            <label className="ui-label">
              통합 검색 (Part No. / Model / Description / Subsystem)
            </label>
            <SearchBar
              value={search}
              onCommit={setSearch}
              placeholder="🔍 예: CS 2060G / GS3060 / 16TB / SFP"
              className="w-full mt-1"
              live
              suggestions={searchSuggestions}
            />
          </div>
          <div className="col-span-3">
            <label className="ui-label">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="ui-input w-full mt-1"
            >
              <option value="all">전체</option>
              <option value="Current">Current</option>
              <option value="EOL Plan">EOL Plan</option>
              <option value="EOL">EOL</option>
            </select>
          </div>
          <div className="col-span-3 text-right">
            <span className="text-sm text-slate-500">
              {filtered.length.toLocaleString('ko-KR')} / {rows.length.toLocaleString('ko-KR')} 건
            </span>
          </div>
        </div>

        {/* 카테고리 chip */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {PRICE_CATEGORIES.map((c) => {
            const active = c.key === category;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setCategory(c.key)}
                className={`btn btn-sm ${c.cls} ${active ? 'btn-secondary' : 'btn-secondary'}`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 표 — 9,500건 전체 렌더는 무겁기 때문에 상위 500건만 표시 (필터 적용 후) */}
      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="data-table w-full text-[13px]">
          <thead>
            <tr>
              <th className="text-center !bg-slate-700 !text-white">카테고리</th>
              <th className="text-left !bg-slate-700 !text-white">Part Number</th>
              <th className="text-left !bg-slate-700 !text-white">Model No.</th>
              <th className="text-left !bg-slate-700 !text-white">Subsystem</th>
              <th className="text-left !bg-slate-700 !text-white">Description</th>
              <th className="text-center !bg-slate-700 !text-white">Form</th>
              <th className="text-center !bg-slate-700 !text-white">Status</th>
              {showCost && <th className="text-right !bg-slate-700 !text-white">공급단가</th>}
              <th className="text-right !bg-slate-700 !text-white">소비자단가</th>
              <th className="text-right !bg-slate-700 !text-white">인상율</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={showCost ? 10 : 9} className="text-center text-slate-400 py-8">
                  {rows.length === 0 ? '아직 가격표가 업로드되지 않았습니다' : '조건에 맞는 부품이 없습니다'}
                </td>
              </tr>
            )}
            {filtered.slice(0, 500).map((p, idx) => {
              const cm = CAT_LOOKUP[p.cat] ?? { label: p.cat, cls: 'bg-slate-100 text-slate-700' };
              return (
                <tr key={`${p.pn}-${p.sheet_name}-${idx}`}>
                  <td className="text-center">
                    <span className={`text-xs px-2 py-0.5 rounded ${cm.cls}`}>{cm.label}</span>
                  </td>
                  <td>
                    <strong className="font-mono text-xs">{p.pn}</strong>
                  </td>
                  <td>{p.model ?? <span className="text-slate-300">-</span>}</td>
                  <td className="text-xs text-slate-600">
                    {p.subsystem ?? <span className="text-slate-300">-</span>}
                  </td>
                  <td className="text-xs">{p.description}</td>
                  <td className="text-center text-xs text-slate-500">{p.form ?? '-'}</td>
                  <td className={`text-center text-xs font-semibold ${statusClass(p.status)}`}>
                    {p.status ?? '-'}
                  </td>
                  {showCost && <td className="text-right num">{fmtKrw(p.supply)}</td>}
                  <td className="text-right num font-semibold">{fmtKrw(p.retail)}</td>
                  <td className={`text-right text-xs ${changeClass(p.change_pct ?? 0)}`}>
                    {p.change_pct === null ? '-' : `${p.change_pct > 0 ? '+' : ''}${p.change_pct.toFixed(2)}%`}
                  </td>
                </tr>
              );
            })}
            {filtered.length > 500 && (
              <tr>
                <td
                  colSpan={showCost ? 10 : 9}
                  className="text-center text-xs text-slate-500 py-3 bg-slate-50"
                >
                  결과 상위 500건만 표시 — 추가로 좁혀 보세요 ({(filtered.length - 500).toLocaleString('ko-KR')}건 숨김)
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showUpload && (
        <Suspense fallback={null}>
          <InfortrendUploadModal
            onClose={() => setShowUpload(false)}
            onUploaded={() => {
              setSelectedMetaId(null); // 업로드 후 자동으로 현재 분기(=방금 올린 것) 표시
              qc.invalidateQueries({ queryKey: ['price-list', 'infortrend'] });
            }}
          />
        </Suspense>
      )}
    </>
  );
}
